import {
  BIOMES,
  teleportCost,
  type TravelRejected,
  type TravelState,
  type ZoneDefinition,
  type ZonePortal,
} from '@shefira/shared';

import { isAction } from '../settings/keybindings.js';
import { closeButton, hudShortcutButton } from './hud.js';
import { levelLabel } from './minimap.js';
import { uiFormat, uiNumber, uiText } from './text.js';
import './travel.css';
import { kitTitle } from './kit.js';

/**
 * The portals' window (ADR 0017), opened with T, the HUD button beside the
 * map, or a click on a village's arch: every village of the zone, the ones
 * used before to travel to for a little gold, the others to be found first.
 * Standing at a portal, one may choose it as the place to get back up.
 *
 * Like every panel it only sends intents: the server knows which portals the
 * player has used and their gold, and says so; the buttons here only grey out
 * what it would refuse, with the same rules.
 */

export interface TravelPanelOptions {
  zone: ZoneDefinition;
  onTeleport: (portalId: string) => void;
  onSetRespawn: (portalId: string) => void;
}

const REFUSALS: Record<TravelRejected['reason'], string> = {
  invalid: 'Demande invalide.',
  'unknown-portal': 'Ce portail n’existe pas.',
  undiscovered: 'Tu dois d’abord utiliser ce portail sur place.',
  here: 'Tu es déjà à ce portail.',
  'missing-gold': 'Il te manque de l’or.',
  'too-far': 'Approche-toi du portail.',
  dead: 'Pas tant que tu es à terre.',
  'in-combat': 'Tu es en plein combat.',
};

export class TravelPanel {
  private readonly root: HTMLElement;
  private readonly where: HTMLElement;
  private readonly list: HTMLElement;
  private readonly message: HTMLElement;
  private readonly button: HTMLButtonElement;
  private travel: TravelState = { discovered: [], respawn: null };
  private gold = 0;
  /** The portal the player stands at, as the client judges it; the server judges again. */
  private here: string | undefined;
  /** Between the click and the server's answer: no second journey meanwhile. */
  private pending = false;
  /** `Date.now()` the player is out of combat, as the server last said. */
  private combatUntil = 0;
  private combatTimer: number | undefined;
  private readonly onKeyDown = (event: KeyboardEvent): void => {
    if (event.metaKey || event.ctrlKey || event.altKey || event.repeat) return;
    if (document.activeElement instanceof HTMLInputElement) return;
    const key = event.key.toLowerCase();
    if (key === 'escape' && !this.root.hidden) {
      this.close();
      return;
    }
    if (!isAction(event, 'travel')) return;
    event.preventDefault();
    if (this.root.hidden) this.open();
    else this.close();
  };

  constructor(private readonly options: TravelPanelOptions) {
    this.root = document.createElement('section');
    this.root.className = 'travel';
    this.root.hidden = true;
    this.root.setAttribute('aria-label', uiText('Portails'));

    const title = document.createElement('h2');
    title.className = 'travel__title';
    kitTitle(title, 'portal');
    title.textContent = uiText('Portails');
    const close = closeButton('Fermer (Échap)', () => {
      this.close();
    });
    title.append(close);

    this.where = document.createElement('p');
    this.where.className = 'travel__where';
    this.list = document.createElement('ul');
    this.list.className = 'travel__list';
    this.message = document.createElement('p');
    this.message.className = 'travel__message';
    this.message.setAttribute('aria-live', 'polite');
    this.root.append(title, this.where, this.list, this.message);
    document.body.append(this.root);

    this.button = hudShortcutButton('portal', 'Portails', 'travel');
    this.button.addEventListener('click', () => {
      if (this.root.hidden) this.open();
      else this.close();
    });
    window.addEventListener('keydown', this.onKeyDown);
  }

  get isOpen(): boolean {
    return !this.root.hidden;
  }

  open(): void {
    this.root.hidden = false;
    this.message.textContent = '';
    this.render();
  }

  close(): void {
    this.root.hidden = true;
  }

  setTravel(state: TravelState): void {
    this.travel = state;
    this.render();
  }

  setGold(gold: number): void {
    if (gold === this.gold) return;
    this.gold = gold;
    this.render();
  }

  /** The portal the player now stands at, if any. */
  setHere(portalId: string | undefined): void {
    if (portalId === this.here) return;
    this.here = portalId;
    this.render();
  }

  /**
   * In combat until then (`Date.now()`): the journeys grey out, with the
   * seconds left, and come back on their own when it ends.
   */
  setCombatUntil(until: number): void {
    this.combatUntil = until;
    this.render();
  }

  /** Whether the server would refuse a journey now for a fight under way. */
  get inCombat(): boolean {
    return Date.now() < this.combatUntil;
  }

  /** The journey went through: the scene tells the chat, the window gets out of the way. */
  showTeleported(): void {
    this.pending = false;
    this.close();
  }

  showRejected(rejected: TravelRejected): void {
    this.pending = false;
    const refusal = REFUSALS[rejected.reason];
    this.say(
      rejected.seconds === undefined
        ? uiText(refusal)
        : uiFormat('{refusal} Attends encore {seconds} s.', {
            refusal: uiText(refusal),
            seconds: rejected.seconds,
          }),
      'refused',
    );
    this.render();
  }

  destroy(): void {
    window.clearTimeout(this.combatTimer);
    window.removeEventListener('keydown', this.onKeyDown);
    this.root.remove();
    this.button.remove();
  }

  private say(text: string, tone: 'refused'): void {
    this.message.textContent = uiText(text);
    this.message.dataset.tone = tone;
  }

  private render(): void {
    window.clearTimeout(this.combatTimer);
    if (this.root.hidden) return;
    const combatMs = this.combatUntil - Date.now();
    if (combatMs > 0) {
      this.say(
        uiFormat('En plein combat : voyage possible dans {seconds} s.', {
          seconds: Math.ceil(combatMs / 1000),
        }),
        'refused',
      );
      // Tick down the seconds, and give the buttons back at the end.
      this.combatTimer = window.setTimeout(
        () => {
          if (!this.inCombat) this.message.textContent = '';
          this.render();
        },
        combatMs % 1000 || 1000,
      );
    }
    const { portals } = this.options.zone;
    const here = portals.find((portal) => portal.id === this.here);
    const respawn = portals.find((portal) => portal.id === this.travel.respawn);
    this.where.textContent = [
      here === undefined
        ? uiText('Loin de tout portail.')
        : uiFormat('Au portail de {place}.', { place: here.name }),
      respawn === undefined
        ? uiText('Tu te relèves au sanctuaire de la capitale.')
        : uiFormat('Tu te relèves à {place}.', { place: respawn.name }),
      uiFormat('Bourse : {gold} or.', { gold: uiNumber(this.gold) }),
    ].join(' ');
    // Easiest first: the order a player walks the world in.
    const byLevel = [...portals].sort(
      (a, b) => BIOMES[a.biome].level.min - BIOMES[b.biome].level.min,
    );
    this.list.replaceChildren(...byLevel.map((portal) => this.row(portal)));
  }

  private row(portal: ZonePortal): HTMLElement {
    const known = this.travel.discovered.includes(portal.id);
    const isHere = portal.id === this.here;
    const biome = BIOMES[portal.biome];
    const item = document.createElement('li');
    item.className = 'travel__row';
    item.dataset.state = isHere ? 'here' : known ? 'known' : 'unknown';

    const name = document.createElement('span');
    name.className = 'travel__name';
    name.textContent = uiText(portal.name);
    const region = document.createElement('span');
    region.className = 'travel__region';
    region.textContent = uiFormat('{region}, {levels}', {
      region: biome.name,
      levels: uiText(levelLabel(biome.level)),
    });
    const label = document.createElement('div');
    label.append(name, region);
    if (portal.id === this.travel.respawn) {
      const tag = document.createElement('span');
      tag.className = 'travel__tag';
      tag.textContent = uiText('Réapparition');
      label.append(tag);
    }
    item.append(label);

    if (isHere) {
      const choose = document.createElement('button');
      choose.type = 'button';
      choose.className = 'travel__go';
      choose.textContent = uiText('Réapparaître ici');
      choose.disabled = portal.id === this.travel.respawn;
      choose.addEventListener('click', () => {
        this.options.onSetRespawn(portal.id);
      });
      item.append(choose);
    } else if (known) {
      const cost = teleportCost(portal);
      const go = document.createElement('button');
      go.type = 'button';
      go.className = 'travel__go';
      go.textContent = uiFormat('Voyager, {cost} or', { cost });
      go.disabled = this.pending || this.gold < cost || this.inCombat;
      go.addEventListener('click', () => {
        this.pending = true;
        this.render();
        this.options.onTeleport(portal.id);
      });
      item.append(go);
    } else {
      const muted = document.createElement('span');
      muted.className = 'travel__muted';
      muted.textContent = uiText('Portail à découvrir');
      item.append(muted);
    }
    return item;
  }
}
